import { TuiAppBar } from '@taiga-ui/layout';
import { TuiTable } from '@taiga-ui/addon-table';
import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// taiga-ui
import {
  TuiDayRangePeriod,
  TuiDataListWrapper,
  TuiAccordion,
  TuiChip,
  TuiComboBox,
  TuiMultiSelect,
  TuiSelect,
  TuiInputDate,
  TuiInputTime,
  TuiInputDateRange,
  TuiChevron,
  TuiInputChip,
} from '@taiga-ui/kit';
import {
  TUI_DEFAULT_MATCHER,
  TuiDay,
  TuiDayRange,
  TuiTime,
  TuiResizable,
  TuiResizer,
} from '@taiga-ui/cdk';
import {
  TuiDataList,
  TuiGroup,
  TuiDropdown,
  TuiIcon,
  TuiButton,
  TuiHint,
  TuiLabel,
  TuiCheckbox,
  TuiInput,
  TuiTextfield,
  TuiCalendar,
  TuiSelectLike,
} from '@taiga-ui/core';

import { IsLoadingService } from '@core/services/is-loading.service';
import { RequestFiltersService } from '@core/services/request/request-filters.service';
import { RequestService } from '@core/services/request/request.service';
import {
  ColumnFiltersTranslations,
  ColumnFiltersTranslationsPayload,
  ColumnsWidths,
  Counterparty,
  InstallerPerson,
  InstallerPersonOrUnassignedOrInstallersGroup,
  InstallersGroup,
  OutkeepSettings,
  Product,
  RequestObdStatus,
  RequestOBDStatusesTranslationsPayload,
  RequestQualityStatusesTranslationsPayload,
  RequestStatus,
  RequestStatusesIdsTranslationsPayload,
  RequestTableColumn,
  RequestListSavedState,
} from '@core/types';
import {
  asapScheduler,
  BehaviorSubject,
  debounceTime,
  map,
  of,
  Subject,
  switchMap,
} from 'rxjs';
import { DeviceDetectorService } from '@core/services/device-detector';
import { LocalStorageService } from '@core/services/local-storage.service';
import {
  FilterObject,
  FilterQuery,
  FilterType,
  type MassFilterKey,
} from '@features/requests/request-list/types';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  INITIAL_DESKTOP_COLUMNS_WIDTH,
  REQUEST_OBD_STATUSES_TRANSLATION_KEY,
  REQUEST_QUALITY_STATUSES_TRANSLATION_KEY,
  REQUEST_STATUSES_TRANSLATION_KEY,
  REQUEST_LIST_STATE_KEY,
} from '@core/constants';
import { MassFilterInputComponent } from '../mass-filter-input/mass-filter-input.component';
import {
  Nullable,
  resolveListRqQuery,
  toFiniteNumbers,
  toMassFilterQueryValue,
  toMassFilterValues,
} from '@core/helpers';
import { endOfDay, startOfDay } from 'date-fns';
import {
  LOGISTIC_REQUEST_TYPE_LABELS,
  LogisticRequestType,
  REQUEST_STATUS_ID_TO_NAME,
} from '@outkeep/shared';

const obdStatusesOptions = [
  { id: RequestObdStatus.HasOBD2, name: 'Есть разъем OBD2' },
  {
    id: RequestObdStatus.HasNoAccessToSocket,
    name: 'Нет доступа / возможности поиска разъема',
  },
  {
    id: RequestObdStatus.HasSocketButNotOBD2,
    name: 'Есть диагностический разъем, но не OBD2',
  },
  { id: RequestObdStatus.HasNoSocket, name: 'Нет диагностического разъема' },
];

const today = TuiDay.currentLocal();
const yesterday = today.append({ day: -1 });
const yesterdayTodayRange = new TuiDayRange(yesterday, today);
const weekAgoRange = new TuiDayRange(today.append({ day: -7 }), today);
const monthAgoRange = new TuiDayRange(today.append({ day: -31 }), today);
const yearAgoRange = new TuiDayRange(today.append({ year: -1 }), today);

@Component({
  selector: '[x-request-list-columns-filters], x-request-list-columns-filters',
  imports: [
    TuiSelectLike,
    TuiTextfield,
    TuiChevron,
    TuiInputChip,
    TuiCalendar,
    CommonModule,
    TuiCheckbox,
    TuiTable,
    FormsModule,
    TuiSelect,
    TuiDropdown,
    TuiDataList,
    TuiMultiSelect,
    TuiDataListWrapper,
    TuiInput,
    TuiInputDateRange,
    TuiAccordion,
    TuiAppBar,
    TuiButton,
    TuiGroup,
    TranslatePipe,
    TuiComboBox,
    ReactiveFormsModule,
    TuiChip,
    TuiHint,
    TuiLabel,
    TuiResizable,
    TuiResizer,
    TuiIcon,
    TuiInputDate,
    TuiInputTime,
    MassFilterInputComponent,
  ],
  templateUrl: './request-list-columns-filters.component.html',
  styleUrl: './request-list-columns-filters.component.scss',
  host: {
    '(document:keyup.enter)': 'onDocumentKeyUp()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestListColumnsFiltersComponent implements OnInit {
  $selectAllRequests = model<undefined | boolean>(false, {
    alias: 'selectAllRequestsInput',
  });
  $selectAllRequestsEvent = output<undefined | boolean>({
    alias: 'selectAllRequestsEventOutput',
  });
  $filterChanged = output<FilterQuery>({
    alias: 'filterChangedOutput',
  });
  $filterIsActive = output<boolean>({
    alias: 'filterIsActiveOutput',
  });
  $tableSettingsChanged = output<OutkeepSettings>({
    alias: 'tableSettingsChanged',
  });

  $emitSearch = output<void>({
    alias: 'emitSearch',
  });

  clearFilters$ = input.required<Subject<void>>();

  $insideDialog = input<boolean>(false);

  $desktopTableColumns = input<RequestTableColumn[]>([], {
    alias: 'desktopTableColumns',
  });

  $desktopTableColumnsWidths = input<ColumnsWidths>(
    INITIAL_DESKTOP_COLUMNS_WIDTH,
    {
      alias: 'desktopTableColumnsWidths',
    },
  );

  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);

  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  public readonly requestService = inject(RequestService);
  public readonly deviceService = inject(DeviceDetectorService);
  private cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly requestFiltersService = inject(RequestFiltersService);
  private readonly resize$ = new Subject<ColumnsWidths>();

  private translations: ColumnFiltersTranslationsPayload &
    RequestQualityStatusesTranslationsPayload &
    RequestOBDStatusesTranslationsPayload &
    RequestStatusesIdsTranslationsPayload = {
    [ColumnFiltersTranslations.Calendar]: {
      yesterday_today_range: 'Вчера-Сегодня',
      week: 'Неделя',
      month: 'Месяц',
      year: 'Год',
    },
    [ColumnFiltersTranslations.NotAssigned]: 'Не назначен',
    [REQUEST_QUALITY_STATUSES_TRANSLATION_KEY]: {
      '0': 'Не проверена',
      '1': 'Заявки без ошибок',
      '2': 'Заявки с ошибками',
      '3': 'Заявки с критическими ошибками',
      '4': 'Заявки с исправленными ошибками',
      '5': 'Заявки с частично исправленными ошибками',
    },
    [REQUEST_OBD_STATUSES_TRANSLATION_KEY]: {
      '1': 'Есть разъем OBD2',
      '2': 'Нет доступа / возможности поиска разъема',
      '3': 'Есть диагностический разъем, но не OBD2',
      '4': 'Нет диагностического разъема',
    },
    [REQUEST_STATUSES_TRANSLATION_KEY]: {
      ...REQUEST_STATUS_ID_TO_NAME,
    },
    [ColumnFiltersTranslations.Yes]: 'Да',
    [ColumnFiltersTranslations.No]: 'Нет',
    [ColumnFiltersTranslations.Group]: 'Группа',
  };

  protected readonly $isDesktop = this.deviceService.$isDesktop;

  // Templates for desktop table columns
  @ViewChild('requestNumber', { static: true })
  requestNumber!: TemplateRef<any>;

  @ViewChild('releaseTime', { static: true })
  releaseTimeTemp!: TemplateRef<any>;

  @ViewChild('releaseDate', { static: true })
  releaseDateTemp!: TemplateRef<any>;

  @ViewChild('releaseDatePeriod', { static: true })
  releaseDatePeriodTemp!: TemplateRef<any>;

  @ViewChild('contract', { static: true })
  contractTemp!: TemplateRef<any>;

  @ViewChild('carModelAndBrand', { static: true })
  carModelAndBrandTemp!: TemplateRef<any>;

  @ViewChild('carPlantNumber', { static: true })
  carPlantNumberTemp!: TemplateRef<any>;

  @ViewChild('vin', { static: true })
  vinTemp!: TemplateRef<any>;

  @ViewChild('projectNumber', { static: true })
  projectNumberTemp!: TemplateRef<any>;

  @ViewChild('subjName', { static: true })
  subjNameTemp!: TemplateRef<any>;

  @ViewChild('deliveryAddress', { static: true })
  deliveryAddressTemp!: TemplateRef<any>;

  @ViewChild('counterparty', { static: true })
  counterpartyTemp!: TemplateRef<any>;

  @ViewChild('requestProduct', { static: true })
  requestProductTemp!: TemplateRef<any>;

  @ViewChild('installers', { static: true })
  installersTemp!: TemplateRef<any>;

  @ViewChild('requestStatus', { static: true })
  requestStatusTemp!: TemplateRef<any>;

  @ViewChild('deliveryName', { static: true })
  deliveryNameTemp!: TemplateRef<any>;

  @ViewChild('createdAt', { static: true })
  createdAtTemp!: TemplateRef<any>;

  @ViewChild('updatedAt', { static: true })
  updatedAtTemp!: TemplateRef<any>;

  @ViewChild('qualityControlStatus', { static: true })
  qualityControlStatusTemp!: TemplateRef<any>;

  @ViewChild('docs', { static: true })
  docsTemp!: TemplateRef<any>;

  @ViewChild('productsDropdownContent')
  set productsDropdownContent(content: ElementRef<HTMLElement> | undefined) {
    this.isProductsDropdownOpen = !!content;
  }

  @ViewChild('installersDropdownContent')
  set installersDropdownContent(content: ElementRef<HTMLElement> | undefined) {
    this.isInstallersDropdownOpen = !!content;
  }

  public columnsTemplates: Nullable<
    Record<RequestTableColumn, TemplateRef<any>>
  > = null;

  // END Templates for desktop table columns

  protected readonly requestStatusStringifyFn = (
    value: RequestStatus,
  ): string =>
    this.translations.request_statuses[
      value.id.toString() as keyof typeof this.translations.request_statuses
    ];

  readonly qualityStatusStringifyFn = (value: {
    id: number;
    name: string;
  }): string =>
    this.translations.quality_statuses[
      value.id.toString() as keyof typeof this.translations.quality_statuses
    ];

  protected readonly booleanStringifyFn = (value: any): string => {
    return value
      ? this.translations[ColumnFiltersTranslations.Yes]
      : this.translations[ColumnFiltersTranslations.No];
  };

  protected readonly obdStatusStringifyFn = (value: number): string => {
    const obdStatus = obdStatusesOptions.find(option => option.id === value);

    if (obdStatus) {
      return this.translations.obd_statuses[obdStatus.id];
    }

    return '';
  };

  protected readonly requestTypeStringifyFn = (
    value: LogisticRequestType,
  ): string => LOGISTIC_REQUEST_TYPE_LABELS[value] ?? value;

  protected readonly productStringifyFn = (value: Product): string =>
    value.name || '';

  protected readonly stringifyFullNameOrGroupNameFn = (value: any): string => {
    return (
      value?.fullName ||
      `${this.translations[ColumnFiltersTranslations.Group]}: ${value?.name}` ||
      ''
    );
  };

  protected readonly stringifyNameFn = (value: Counterparty): string => {
    return value.name || '';
  };

  public cpSearch: string | null = '';

  // inner object with all filters
  public filterObject: FilterObject = {};
  // query object for output to parent component for request
  public filterQuery: FilterQuery = {};

  // интервалы дат для date picker`а
  public dateRanges: TuiDayRangePeriod[] = [];

  public filterType = FilterType;

  public requestTypeOptions: LogisticRequestType[] = [];

  // Статус заявки
  public requestStatuses: RequestStatus[] = [];

  // СЛС
  public installers: InstallerPersonOrUnassignedOrInstallersGroup[] = [];
  public installersGroups: InstallersGroup[] = [];
  public installerSearch: string | null = '';
  public isInstallersDropdownOpen = false;

  // Товары\услуги
  public products: Product[] = [];
  public productSearch: string | null = '';
  public isProductsDropdownOpen = false;

  // Контрагенты
  public counterparties: Counterparty[] = [];

  // Статус ОКК
  public qualityControlStatuses = [
    {
      id: 0,
      name: 'not_verified',
    },
    {
      id: 1,
      name: 'requests_without_errors',
    },
    {
      id: 2,
      name: 'requests_with_errors',
    },
    {
      id: 3,
      name: 'requests_with_critical_errors',
    },
    {
      id: 4,
      name: 'requests_with_fixed_errors',
    },
    {
      id: 5,
      name: 'requests_with_partially_fixed_errors',
    },
  ];

  // Статус OBD
  readonly obdStatusesOptions = obdStatusesOptions;

  public ngOnInit(): void {
    this.init();
    this.initColumns();
    this.subscribeToClearFilters();
    this.resize$
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((tableColumnsWidths: ColumnsWidths) => {
        this.$tableSettingsChanged.emit({ tableColumnsWidths });
      });
  }

  private init() {
    this.outkeepTranslateService
      .translate([
        REQUEST_QUALITY_STATUSES_TRANSLATION_KEY,
        REQUEST_STATUSES_TRANSLATION_KEY,
        REQUEST_OBD_STATUSES_TRANSLATION_KEY,
        ...Object.values(ColumnFiltersTranslations),
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translations => {
        this.translations = translations;
        // set date ranges for date picker with translations
        this.dateRanges = [
          new TuiDayRangePeriod(
            yesterdayTodayRange,
            this.translations[ColumnFiltersTranslations.Calendar][
              'yesterday_today_range'
            ],
            () => yesterdayTodayRange.toString(),
          ),
          new TuiDayRangePeriod(
            weekAgoRange,
            this.translations[ColumnFiltersTranslations.Calendar]['week'],
            () => weekAgoRange.toString(),
          ),
          new TuiDayRangePeriod(
            monthAgoRange,
            this.translations[ColumnFiltersTranslations.Calendar]['month'],
            () => monthAgoRange.toString(),
          ),
          new TuiDayRangePeriod(
            yearAgoRange,
            this.translations[ColumnFiltersTranslations.Calendar]['year'],
            () => yearAgoRange.toString(),
          ),
        ];
        this.initFilters();
      });
  }

  private initFilters() {
    this.requestFiltersService.filtersData$
      .pipe(
        map(
          ({
            statuses,
            installers,
            counterparties,
            installersGroups,
            requestTypes,
            products,
          }) => {
            if (
              !statuses ||
              !installers ||
              !counterparties ||
              !installersGroups ||
              !requestTypes ||
              !products
            ) {
              return false;
            }
            // this shit fires twice on navigation back and forward so we need to check if data was already mapped
            if (this.requestStatuses.length === 0) {
              // map data
              this.requestStatuses = statuses;
              this.installers.push(
                {
                  id: null,
                  fullName:
                    this.translations[ColumnFiltersTranslations.NotAssigned],
                  lastName:
                    this.translations[ColumnFiltersTranslations.NotAssigned],
                },
                ...installers,
              );
              this.installersGroups = installersGroups;
              this.counterparties = counterparties;
              this.requestTypeOptions = requestTypes;
              this.products = products;

              return true;
            }

            return false;
          },
        ),
        switchMap(dataReceived => {
          if (!dataReceived) {
            return of(null);
          }

          return this.route.queryParams.pipe(
            takeUntilDestroyed(this.destroyRef),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(params => {
        if (!params) {
          return;
        }

        // фильтр из адреса; массовый набор лежит в сохранённом состоянии
        const rqQuery = resolveListRqQuery(
          params,
          this.localStorageService.getItem<RequestListSavedState>(
            REQUEST_LIST_STATE_KEY,
          )?.rqQuery,
        ) as FilterQuery;

        const rqQueryKeys = Object.keys(rqQuery);

        if (
          rqQueryKeys.length === 0 ||
          (rqQueryKeys.length === 1 &&
            rqQueryKeys[0] === 'contractorCounterpartyId')
        ) {
          this.$filterIsActive.emit(false);
        } else {
          this.$filterIsActive.emit(true);
        }

        this.filterObject = {
          ...this.filterObject,
          requestStatus:
            typeof rqQuery.statusId === 'number'
              ? this.requestStatuses.find(
                  status => status.id === rqQuery.statusId,
                )
              : undefined,
          qualityControlStatus: this.qualityControlStatuses.find(
            status => status.id === rqQuery.qualityControlStatus,
          ),
          requestType: this.parseRequestTypes(rqQuery.requestType),
          installers: this.parseInstallers(rqQuery),
          products: this.parseProducts(rqQuery),
          requestNumber: rqQuery.requestNumber,
          projectNumber: rqQuery.projectNumber,
          counterpartyOwner: this.counterparties.find(
            cp => cp.id === rqQuery.counterpartyOwnerId,
          ),
          contractorCounterpartyId: rqQuery.contractorCounterpartyId,
          // фильтр «Исполнитель» (модалка): прокидываем подразделение как есть,
          // чтобы оно не терялось при изменении колоночных фильтров
          contractorCounterpartySubdivisionId:
            rqQuery.contractorCounterpartySubdivisionId,
          contract: toMassFilterValues(rqQuery.contract),
          carPlantNumber: rqQuery.carPlantNumber,
          subjName: rqQuery.subjName,
          deliveryAddress: rqQuery.deliveryAddress,
          deliveryName: rqQuery.deliveryName,
          releaseDate: rqQuery.releaseDate
            ? TuiDay.fromLocalNativeDate(new Date(rqQuery.releaseDate))
            : undefined,
          releaseTime: rqQuery.releaseTime
            ? TuiTime.fromString(rqQuery.releaseTime)
            : undefined,
          releaseDatePeriod:
            rqQuery.releaseDatePeriod?.startDate &&
            rqQuery.releaseDatePeriod?.endDate
              ? new TuiDayRange(
                  TuiDay.fromLocalNativeDate(
                    new Date(rqQuery.releaseDatePeriod?.startDate),
                  ),
                  TuiDay.fromLocalNativeDate(
                    new Date(rqQuery.releaseDatePeriod?.endDate),
                  ),
                )
              : undefined,
          carModelAndBrand: rqQuery.carModelAndBrand,
          vin: toMassFilterValues(rqQuery.vin),
          createdViaApi: rqQuery.createdViaApi,
          inventoryDocUploaded: rqQuery.inventoryDocUploaded,
          leasingProduct: rqQuery.leasingProduct,
          extendedProgram: rqQuery.extendedProgram,
          manufacturerCompany: rqQuery.manufacturerCompany,
          manufacturerCountry: rqQuery.manufacturerCountry,
          isForCreateRequests: rqQuery.isForCreateRequests,
          obdStatus: rqQuery.obdStatus,
        };
        this.filterQuery = this.updateWithExistingFilters({});
        this.cdr.markForCheck();
      });
  }

  private initColumns() {
    // Initialize columns with templates
    this.columnsTemplates = {
      requestNumber: this.requestNumber,
      releaseTime: this.releaseTimeTemp,
      releaseDate: this.releaseDateTemp,
      releaseDatePeriod: this.releaseDatePeriodTemp,
      contract: this.contractTemp,
      car: this.carModelAndBrandTemp,
      carPlantNumber: this.carPlantNumberTemp,
      vin: this.vinTemp,
      projectNumber: this.projectNumberTemp,
      subjName: this.subjNameTemp,
      deliveryAddress: this.deliveryAddressTemp,
      counterPartyName: this.counterpartyTemp,
      products: this.requestProductTemp,
      installerShort: this.installersTemp,
      statusId: this.requestStatusTemp,
      deliveryName: this.deliveryNameTemp,
      createdAt: this.createdAtTemp,
      updatedAt: this.updatedAtTemp,
      qualityControlStatus: this.qualityControlStatusTemp,
      docs: this.docsTemp,
    };
  }

  private parseRequestTypes(value: unknown): LogisticRequestType[] | undefined {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    const requestTypes = values.filter(
      (item): item is LogisticRequestType =>
        typeof item === 'string' &&
        this.requestTypeOptions.includes(item as LogisticRequestType),
    );

    return requestTypes.length ? requestTypes : undefined;
  }

  private parseInstallers(
    rqQuery: any,
  ): InstallerPersonOrUnassignedOrInstallersGroup[] {
    const installerGroupsToAdd: InstallersGroup[] = [];

    // If there are installers groups, we need to check if all installers from the group are selected then select this group
    if (this.installersGroups.length) {
      const selectedInstallerIds = (rqQuery.installerId ?? []) as number[];

      this.installersGroups.forEach(group => {
        const groupInstallerIds = group.installers.map(inst => inst.id);

        const allIncluded = groupInstallerIds.every(id =>
          selectedInstallerIds.includes(id),
        );

        if (allIncluded && groupInstallerIds.length > 0) {
          installerGroupsToAdd.push(group);
        }
      });
    }

    return this.installers
      .filter(installer => rqQuery.installerId?.includes(installer.id))
      .concat(installerGroupsToAdd);
  }

  private parseProducts(rqQuery: FilterQuery): Product[] {
    const productIds = toFiniteNumbers(rqQuery.productsIds ?? []);

    return this.products.filter(product => productIds.includes(product.id));
  }

  private subscribeToClearFilters() {
    this.clearFilters$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.filterObject = {};
        this.cdr.markForCheck();
      });
  }

  onSelectAllRequests() {
    this.$selectAllRequestsEvent.emit(this.$selectAllRequests());
  }

  onProductsFilterModelChange() {
    if (this.isProductsDropdownOpen) {
      return;
    }

    this.onFilterChange(FilterType.products);
  }

  onDocumentKeyUp() {
    if (!this.$isDesktop() || this.$insideDialog()) {
      return;
    }

    if (this.isProductsDropdownOpen) {
      this.onFilterChange(FilterType.products);
    } else if (this.isInstallersDropdownOpen) {
      this.onFilterChange(FilterType.installers);
    }
  }

  /**
   * Поля массовых фильтров с недопустимым набором. Пока набор недопустим, он
   * не доходит до filterQuery: поиск продолжает идти по прежнему значению.
   */
  private readonly $invalidMassFilters = signal<ReadonlySet<MassFilterKey>>(
    new Set(),
  );
  /**
   * Последний допустимый набор по каждому массовому полю. Недопустимый набор
   * всё равно живёт в filterObject — иначе поле ввода затирало бы то, что
   * пользователь набрал, — но собирать из него запрос нельзя: смена любого
   * соседнего фильтра пересобирает filterQuery из filterObject и отправила
   * бы недопустимый набор на сервер.
   */
  private readonly lastValidMassFilters = new Map<MassFilterKey, string[]>();

  onMassFilterInvalidChange(key: MassFilterKey, invalid: boolean): void {
    this.$invalidMassFilters.update(keys => {
      if (keys.has(key) === invalid) {
        return keys;
      }

      const next = new Set(keys);

      if (invalid) {
        next.add(key);
      } else {
        next.delete(key);
      }

      return next;
    });
  }

  /**
   * Массовый фильтр: на десктопе поиск запускается по применению (потеря
   * фокуса или Ctrl+Enter), а не на каждое нажатие клавиши — Enter в поле
   * набора значений переносит строку.
   */
  onMassFilterValuesChange(key: MassFilterKey, values: string[]): void {
    this.filterObject[key] = values;

    if (this.$invalidMassFilters().has(key)) {
      return;
    }

    this.lastValidMassFilters.set(key, values);

    if (!this.$isDesktop() || this.$insideDialog()) {
      this.onFilterChange(FilterType[key]);
    }
  }

  onMassFilterApply(key: MassFilterKey): void {
    if (this.$invalidMassFilters().has(key)) {
      return;
    }

    this.onFilterChange(FilterType[key]);
  }

  /** Значение массового фильтра, пригодное для запроса. */
  private resolveMassFilterValue(key: MassFilterKey): string[] | undefined {
    return this.$invalidMassFilters().has(key)
      ? this.lastValidMassFilters.get(key)
      : this.filterObject[key];
  }

  // Main function to handle filter changes and updates the filter query accordingly to send it vie eventEmitter to parent component.
  onFilterChange(type?: FilterType) {
    if (!type) {
      return;
    }
    // в модалке/шторке поиск запускается кнопкой «Применить», а не на каждое изменение
    const shouldSearchImmediately = this.$isDesktop() && !this.$insideDialog();
    // Helper func. Updates the filter query object and emits it to the parent component with debounce or not
    const updateFilterQuery = (key: keyof FilterQuery, value: any) => {
      if (value || value === 0 || typeof value === 'boolean') {
        this.filterQuery[key] = value;
        this.filterQuery = this.updateWithExistingFilters(this.filterQuery);
      } else {
        delete this.filterQuery[key];
      }
      this.$filterChanged.emit(this.filterQuery);

      if (shouldSearchImmediately) {
        asapScheduler.schedule(() => {
          this.$emitSearch.emit();
        });
      }
    };

    switch (type) {
      case FilterType.requestStatus: {
        updateFilterQuery('statusId', this.filterObject.requestStatus?.id);

        return;
      }
      case FilterType.requestType: {
        updateFilterQuery(
          'requestType',
          this.filterObject.requestType?.length
            ? this.filterObject.requestType
            : null,
        );

        return;
      }
      case FilterType.qualityControlStatus: {
        updateFilterQuery(
          'qualityControlStatus',
          this.filterObject.qualityControlStatus?.id,
        );

        return;
      }
      case FilterType.installers: {
        // get installers from groups if exists
        const installersIds: (number | null)[] =
          this.filterObject.installers?.flatMap(item =>
            'installers' in item && Array.isArray(item.installers)
              ? item.installers.map(inst => inst.id)
              : [item.id],
          ) || [];

        updateFilterQuery(
          'installerId',
          installersIds?.length ? installersIds : null,
        );

        return;
      }
      case FilterType.products: {
        updateFilterQuery(
          'productsIds',
          this.filterObject.products?.length
            ? this.filterObject.products.map(product => product.id)
            : null,
        );

        return;
      }
      case FilterType.requestNumber: {
        updateFilterQuery('requestNumber', this.filterObject.requestNumber);

        return;
      }
      case FilterType.projectNumber: {
        updateFilterQuery('projectNumber', this.filterObject.projectNumber);

        return;
      }
      case FilterType.counterpartyOwnerId: {
        updateFilterQuery(
          'counterpartyOwnerId',
          this.filterObject.counterpartyOwner?.id,
        );

        return;
      }
      case FilterType.contract: {
        updateFilterQuery(
          'contract',
          toMassFilterQueryValue(this.filterObject.contract ?? []),
        );

        return;
      }
      case FilterType.carModelAndBrand: {
        updateFilterQuery(
          'carModelAndBrand',
          this.filterObject.carModelAndBrand,
        );

        return;
      }
      case FilterType.carPlantNumber: {
        updateFilterQuery('carPlantNumber', this.filterObject.carPlantNumber);

        return;
      }
      case FilterType.vin: {
        updateFilterQuery(
          'vin',
          toMassFilterQueryValue(this.filterObject.vin ?? []),
        );

        return;
      }
      case FilterType.subjName: {
        updateFilterQuery('subjName', this.filterObject.subjName);

        return;
      }
      case FilterType.deliveryAddress: {
        updateFilterQuery('deliveryAddress', this.filterObject.deliveryAddress);

        return;
      }

      case FilterType.deliveryName: {
        updateFilterQuery('deliveryName', this.filterObject.deliveryName);

        return;
      }
      case FilterType.releaseTime: {
        updateFilterQuery(
          'releaseTime',
          this.filterObject.releaseTime?.toString(),
        );

        return;
      }
      case FilterType.releaseDate: {
        const value = this.filterObject.releaseDate;

        if (!value) {
          delete this.filterQuery.releaseDate;
          this.$filterChanged.emit(this.filterQuery);
        } else {
          updateFilterQuery(
            'releaseDate',
            startOfDay(value!.toLocalNativeDate()).toISOString(),
          );
        }

        return;
      }

      case FilterType.releaseDatePeriod: {
        const value = this.filterObject.releaseDatePeriod;

        if (!value) {
          delete this.filterQuery.releaseDatePeriod;
          this.$filterChanged.emit(this.filterQuery);
        } else {
          const { from, to } = value;

          this.filterQuery.releaseDatePeriod = {
            startDate: startOfDay(from.toLocalNativeDate()).toISOString(),
            endDate: endOfDay(to.toLocalNativeDate()).toISOString(),
          };
          this.filterQuery = this.updateWithExistingFilters(this.filterQuery);
          this.$filterChanged.emit(this.filterQuery);
        }
        if (shouldSearchImmediately) {
          asapScheduler.schedule(() => {
            this.$emitSearch.emit();
          });
        }

        return;
      }
      case FilterType.createdViaApi: {
        updateFilterQuery('createdViaApi', this.filterObject.createdViaApi);

        return;
      }
      case FilterType.inventoryDocUploaded: {
        updateFilterQuery(
          'inventoryDocUploaded',
          this.filterObject.inventoryDocUploaded,
        );

        return;
      }
      case FilterType.leasingProduct: {
        updateFilterQuery('leasingProduct', this.filterObject.leasingProduct);

        return;
      }

      case FilterType.extendedProgram: {
        updateFilterQuery('extendedProgram', this.filterObject.extendedProgram);

        return;
      }

      case FilterType.manufacturerCompany: {
        updateFilterQuery(
          'manufacturerCompany',
          this.filterObject.manufacturerCompany,
        );

        return;
      }

      case FilterType.manufacturerCountry: {
        updateFilterQuery(
          'manufacturerCountry',
          this.filterObject.manufacturerCountry,
        );

        return;
      }

      case FilterType.isForCreateRequests: {
        updateFilterQuery(
          'isForCreateRequests',
          this.filterObject.isForCreateRequests,
        );

        return;
      }

      case FilterType.obdStatus: {
        updateFilterQuery('obdStatus', this.filterObject.obdStatus);

        return;
      }
      default:
        return;
    }
  }

  private updateWithExistingFilters(filterQuery: FilterQuery): FilterQuery {
    const existingFilters = Object.fromEntries(
      Object.entries(this.filterObject)
        .filter(
          ([_, value]) => value && (!Array.isArray(value) || value.length),
        )
        .map(([key, value]) => {
          if (key === 'contract' || key === 'vin') {
            value = this.resolveMassFilterValue(key);
          }
          if (key === 'requestStatus') {
            key = 'statusId';
            value = value.id;
          }
          if (key === 'counterpartyOwner') {
            key = 'counterpartyOwnerId';
            value = value.id;
          }
          if (key === 'installers') {
            key = 'installerId';
            value = value.map(
              (installer: InstallerPersonOrUnassignedOrInstallersGroup) =>
                installer.id,
            );
          }
          if (key === 'products') {
            key = 'productsIds';
            value = value.map((product: Product) => product.id);
          }
          if (key === 'releaseDatePeriod') {
            key = 'releaseDatePeriod';
            const { from, to } = value;

            value = {
              startDate: startOfDay(from.toLocalNativeDate()).toISOString(),
              endDate: endOfDay(to.toLocalNativeDate()).toISOString(),
            };
          }

          if (key === 'qualityControlStatus') value = value.id;

          return [key, value];
        })
        // пустые значения отбрасываем после подстановки: у недопустимого
        // массового набора подставленного значения может не быть вовсе
        .filter(
          ([_, value]) => value && (!Array.isArray(value) || value.length),
        ),
    );

    return { ...existingFilters, ...filterQuery };
  }

  @pure
  filterCounterparty(search: string | null) {
    if (!search) {
      return this.counterparties;
    }

    return this.counterparties.filter(cp =>
      TUI_DEFAULT_MATCHER(cp.name, search || ''),
    );
  }

  @pure
  filterInstallers(search: string | null): InstallerPerson[] {
    return (this.installers as InstallerPerson[]).filter(installer =>
      TUI_DEFAULT_MATCHER(
        (installer as InstallerPerson).fullName,
        search || '',
      ),
    );
  }

  @pure
  filterProducts(search: string | null): Product[] {
    return this.products.filter(product =>
      TUI_DEFAULT_MATCHER(product.name, search || ''),
    );
  }

  @pure
  filterCounterparties(search: string | null) {
    return this.counterparties.filter(counterParty =>
      TUI_DEFAULT_MATCHER(counterParty.name, search || ''),
    );
  }

  onResized(col: RequestTableColumn, event: readonly [x: number, y: number]) {
    const [width] = event;
    const newColumnsWidths = {
      ...this.$desktopTableColumnsWidths(),
      [col]: width,
    };

    this.resize$.next(newColumnsWidths);
  }
}
